import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { SolutionSource, SolutionSourceState } from '../shared/solution-source.js';
import { PendingTask } from './api/pending-tasks/pending-task.js';
import { TaskType } from './api/task-types/task-main.js';
import { BoundingBox } from './BoundingBox.js';
import { ConfirmationProgress, ConfirmationProgressStatus } from './confirmation-progress.js';
import {
  AnswerExtractionProgress,
  SolutionProgress,
  StepState,
  TaskExtractionProgress,
} from './extraction-progress.js';
import { Submission } from './submission.js';
import { computeAggregate } from './utils/points-model.js';
import { collectGradingSchemeUncertainTaskPaths } from './utils/solution-completeness.js';

export type ExamStatus =
  | 'created'
  | 'submissionsUploaded'
  | 'examTasksDefined'
  | 'submissionsExtracted'
  | 'masterSolutionNeesdsReview'
  | 'masterSolutionDefined'
  | 'gradingSchemeNeedsReview'
  | 'gradingSchemeDefined'
  | 'gradingInProgress'
  | 'gradingNeedsReview'
  | 'graded';

registerEnum({ enum: TaskType, name: 'TaskType' });

/** One generated figure, persisted as its own `ImageFile` — see `Exam.figureAssets`. */
@Entity()
export class ExamFigureAsset extends AdornisEntity {
  static override _class = 'ExamFigureAsset';

  // Every concrete AdornisEntity subclass has to redeclare `_id` itself — see BoundingBox.ts for the same
  // default-generation pattern. NULLABLE, unlike BoundingBox's: `getExamByIdQuery` returns the raw Mongo
  // document straight through with no `new Exam(...)` construction step, so the default below only ever
  // fires for a freshly-built ExamFigureAsset, never for one already sitting in the database — and every
  // exam persisted before this field existed has entries with no `_id` at all. A non-nullable `ID` type
  // rejects those on read; nothing here actually needs `_id` to mean anything (materialId does the real
  // identifying), so there is no reason to require it.
  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() }) _id: Maybe<string>;

  /** The material id `#figure-block`'s `path:` argument embeds (e.g. "m1"), not a database id. */
  @Field(() => String)
  materialId!: Maybe<string>;

  /** The `ImageFile` id — resolve with `getFileByIDQuery(fileId)` and `.getServeLink()`. */
  @Field(() => String)
  fileId!: Maybe<string>;
}

/**
 * One generated PLOT, persisted as its own typst source — see `Exam.plotAssets`. Unlike a photo, a plot
 * has no durable file behind it (see exam-plots.ts): what a compile produced was always just SVG text
 * rendered from this code, so this is the thing worth keeping — small, and it regenerates the exact same
 * SVG on demand, rather than a one-time raster/vector that a later recompile has no way to reproduce.
 */
@Entity()
export class ExamPlotAsset extends AdornisEntity {
  static override _class = 'ExamPlotAsset';

  // Same nullable-`_id` reasoning as ExamFigureAsset above — materialId is the real identifier here too.
  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() }) _id: Maybe<string>;

  /** The material id `#figure-block`'s `path:` argument embeds (e.g. "m1"), not a database id. */
  @Field(() => String)
  materialId!: Maybe<string>;

  /** The standalone typst snippet (`@preview/cetz`/`cetz-plot`) that renders this plot — see exam-plots.ts. */
  @Field(() => String)
  code!: Maybe<string>;
}

/**
 * One aggregated cost bucket for a single kind of LLM call (the `source` label already passed to
 * `addCostToExam`) — see `Exam.costBySource`. Embedded with no own `_id`, so its numeric fields MUST use
 * `default: v => v ?? 0`, never a plain `= 0` initializer (see CLAUDE.md §6) — this entity has no stable
 * `_id` to self-heal a wrong value the way a top-level MongoEntity would.
 */
@Entity()
export class CostBySourceEntry extends AdornisEntity {
  static override _class = 'CostBySourceEntry';

  @Field(() => String)
  source!: string;

  @Field(() => Float, { default: v => v ?? 0 })
  totalCost!: number;

  @Field(() => Int, { default: v => v ?? 0 })
  callCount!: number;
}

@Entity()
export class Exam extends MongoEntity {
  static override _class = 'Exam';
  static override _collectionName = 'exams';

  /** ID of the user who created/owns this exam */
  @Field(() => String)
  userID: Maybe<string>;

  @Field(() => String)
  conversationId!: string;

  @Field(() => String)
  createdAt!: string;

  @Field(() => String)
  school: Maybe<string>;

  @Field(() => String)
  title!: Maybe<string>;

  @Field(() => String)
  subject: Maybe<string>;

  @Field(() => String)
  topic: Maybe<string>;

  @Field(() => String)
  class: Maybe<string>;

  /** `_id` of the linked `ClassRoom` (db/classroom.ts), when one has been matched/created — see
   * `linkExamToClassroom` (db/utils/classroom.ts), the single place that resolves and writes this.
   * `class`/`subject` above remain the human-readable display values; this is the actual foreign key
   * used to reliably show the exam under its class in the teacher's class section (see
   * get-class-exam-overview.ts), instead of relying purely on a name+subject string match that silently
   * breaks if either one ever drifts from the classroom's own values. `Maybe` because older exams
   * predate this field and exams whose owner has no teacher profile (`linked: 'unresolved'`) never get
   * a real classroom to reference. */
  @Field(() => String)
  classRoomID: Maybe<string>;

  @Field(() => String)
  teacher: Maybe<string>;

  @Field(() => String)
  group: Maybe<string>;

  @Field(() => String)
  status!: ExamStatus;

  @Field(() => [String])
  masterSolutionIds: Maybe<string[]>;

  @Field(() => [String])
  gradingSchemeIds!: Maybe<string[]>;

  @Field(() => [String])
  blankExamIds!: Maybe<string[]>;

  /**
   * The typst source of a CREATED exam — the single artifact the whole exam is derived from. The task
   * tree, the PDF and the page images above are all outputs of compiling this (see
   * api/exam-creation/compile-exam.ts), so it is the thing the agent and the teacher's editor both edit,
   * and the thing to recompile when either of them changes something. Absent on uploaded exams.
   */
  @Field(() => String)
  examSource: Maybe<string>;

  /** The PDFFile id of the last compile of `examSource` — what the teacher prints. */
  @Field(() => String)
  examSourcePdfId: Maybe<string>;

  /**
   * Serialized `Record<taskOrMaterialId, BlockBoundingBox>` from the last compile — see
   * `computeBoundingBoxes`. The rich-content SVG itself is NOT persisted here: once a figure is involved it
   * embeds the image as base64, and a page or two of those pushes the whole `Exam` document (tasks,
   * additionalInformation, this field, all $set together) past what a single write can carry — silently
   * losing the compile's other results, not just the preview. The editor fetches it fresh instead, on
   * demand, via `getExamSvgQuery` — see exam-editor-api.ts.
   */
  @Field(() => String)
  examLayoutJson: Maybe<string>;

  /**
   * Serialized `ExamPlan` (see exam-creation-agent.ts) from the last time this exam was PLANNED — by the
   * initial creation, or by a full replan a refinement fell back to. Read back by `loadRefinementContext`
   * so a later "adjust this exam" instruction refines the actual plan the current tasks were written
   * from, rather than treating the exam as source with no plan behind it (which the change planner can
   * still cope with — see `requestChangePlan` — but only by forcing a full replan instead of a patch).
   * Absent on an exam created before this field existed, or one with no source at all.
   */
  @Field(() => String)
  examPlanJson: Maybe<string>;

  @Field(() => [ExamTask])
  tasks: Maybe<ExamTask[]>;

  @Field(() => [AdditionalInfo])
  additionalInformation: Maybe<AdditionalInfo[]>;

  /**
   * The generated-exam figures a compile produced, by the material id `#figure-block`'s `path:` argument
   * embeds (e.g. "m1") — the only way to show a figure standalone (for the editor's draggable callout
   * points) rather than only baked into the compiled PDF's page images. Rewritten wholesale on every
   * recompile, same as `tasks`/`additionalInformation`: stale entries from a since-removed figure are not
   * worth reconciling individually when the whole set is already being regenerated.
   */
  @Field(() => [ExamFigureAsset])
  figureAssets: Maybe<ExamFigureAsset[]>;

  /**
   * The generated-exam PLOTS a compile produced, by the material id `#figure-block`'s `path:` argument
   * embeds — the typst source, not an SVG (see `ExamPlotAsset`), since a plot has no `ImageFile` behind it
   * the way a `figureAssets` photo does. Rewritten wholesale on every recompile, same as `figureAssets`.
   */
  @Field(() => [ExamPlotAsset])
  plotAssets: Maybe<ExamPlotAsset[]>;

  /**
   * Language-use grading rules extracted from an uploaded grading scheme, scoped to the task(s) they apply
   * to (see LanguageUseScheme.dependsOn). A rule scoped to exactly one task is merged directly onto that
   * task's GradingScheme (grammarCriteria/grammarMaxPoints) instead of appearing here. Rewritten
   * (possibly to []) on every GS extraction so a re-upload clears stale rules.
   */
  @Field(() => [LanguageUseScheme])
  languageUseSchemeRules: Maybe<LanguageUseScheme[]>;

  /**
   * Counter backing `LanguageUseScheme.displayNumber` — every allocation does one atomic `$inc` against
   * this field (see `allocateLanguageUseSchemeDisplayNumbers`), so a number is never handed out twice even
   * across deletions. Gaps are fine (a number is simply never reused); uniqueness is what matters.
   */
  @Field(() => Int, { default: v => v ?? 0 })
  languageUseSchemeSeq!: number;

  @Field(() => Float)
  totalCost: Maybe<number>;

  /**
   * Cost broken down by kind of LLM call, aggregated per `source` label rather than one entry per call —
   * see `addCostToExam`'s atomic upsert-or-increment. `default` covers a freshly-created exam (constructed
   * locally, not over the network, so the default actually runs — see AdornisEntity's `isFromNetwork`
   * check) but NOT every path that can produce `null` here (a resave of an exam reconstructed from an
   * existing document skips this default entirely); `addCostToExam` normalizes `null` -> `[]` itself before
   * writing, which is the guarantee that actually holds regardless of how the `null` got there.
   */
  @Field(() => [CostBySourceEntry], { default: v => v ?? [] })
  costBySource: Maybe<CostBySourceEntry[]>;

  @Field(() => String)
  gradingScaleJson: Maybe<string>;

  /** Per-exam Fehlerindex deduction settings (serialized GrammarFiSettings, see shared/grammar-grading.ts). */
  @Field(() => String)
  grammarFiSettingsJson: Maybe<string>;

  @DataLoadedField(() => [Submission], ({ _id }) => ({ examId: _id }))
  submissions: Maybe<Submission[]>; // Optional: Liste der Einreichungen, kann auch in einer separaten Collection gespeichert werden

  @Field(() => ExamState)
  examState: Maybe<ExamState>;
}

@Entity()
export class AdditionalInfo extends AdornisEntity {
  static override _class = 'AdditionalInfo';

  @Field(() => String)
  _id!: Maybe<string>;

  @Field(() => String)
  content!: Maybe<string>;

  @Field(() => [BoundingBox])
  boundingBoxes!: Maybe<BoundingBox[]>;
}

export type DescriptionWarningCode = 'text_cutoff' | 'missing_information' | 'image_unreadable';

@Entity()
export class ExamTask extends AdornisEntity {
  static override _class = 'ExamTask';
  @Field(() => String)
  _id!: string;

  @Field(() => String)
  taskNumber!: string;

  @Field(() => String)
  description!: string;

  @Field(() => MasterSolution)
  masterSolution!: Maybe<MasterSolution>;

  @Field(() => GradingScheme)
  gradingScheme!: Maybe<GradingScheme>;

  @Field(() => TaskType)
  taskType!: TaskType;

  @Field(() => [String])
  additionalInfoIds!: Maybe<string[]>;

  /**
   * Figures placed directly in this task's own body, no `#material` wrapper — raw `<gradias>` ids, not the
   * generated-image ids on `boundingBoxes` below. See `ExamTaskNode.inlineFigureIds` in exam-structure.ts
   * for why these are kept separate from `additionalInfoIds` and what they're used for.
   */
  @Field(() => [String])
  inlineFigureIds!: Maybe<string[]>;

  @Field(() => [BoundingBox])
  boundingBoxes!: Maybe<BoundingBox[]>;

  @Field(() => Boolean)
  needsAnswer!: Maybe<boolean>;

  /**
   * The DECLARED TOTAL for a task — a leaf's points, or a task-with-subtasks' whole subtree total. Written
   * by extraction and the structure editor. An answerable task's OWN points (a leaf's points; an answerable
   * parent's own-answer worth, = total − children) live in `gradingScheme.maxPoints`, so a hybrid parent
   * carries BOTH: the total here and its own in the scheme. No ghost nodes — every task stays a normal node.
   */
  @Field(() => Float)
  declaredMaxPoints!: Maybe<number>;

  /**
   * DERIVED, never stored: the real total for this task's whole subtree — own points (if answerable) +
   * the aggregate of every child. Computed on read by the points model, so it can never go stale and
   * needs no sync. Read wherever a task total is needed. (Because it has a resolver, baseql excludes it
   * from input types — it cannot be saved; the declared value above is the writable one.)
   */
  @Field(() => Float, {
    resolve(this: any) {
      const node = this;
      return () => computeAggregate(node);
    },
  })
  aggregateMaxPoints!: Maybe<number>;

  @Field(() => [ExamTask])
  subTasks!: Maybe<ExamTask[]>;

  @Field(() => [String])
  descriptionWarningCodes: Maybe<string[]>;
}

@Entity()
export class MasterSolution extends AdornisEntity {
  static override _class = 'MasterSolution';

  static override resolveID(data: any) {
    return data.taskNumber;
  }

  @Field(() => String)
  examId!: string;

  @Field(() => String)
  taskNumber: Maybe<string>;

  @Field(() => String)
  content!: string;

  @Field(() => [String])
  feedback!: Maybe<string[]>;

  @Field(() => [BoundingBox])
  boundingBoxes!: Maybe<BoundingBox[]>;

  @Field(() => Float)
  confidence: Maybe<number>;

  @Field(() => Boolean)
  isApproved: Maybe<boolean>;

  /** Where this task's content came from — see SolutionSource. Absent on data written before the field existed. */
  @Field(() => String)
  source: Maybe<SolutionSource>;
}

@Entity()
export class GradingScheme extends AdornisEntity {
  static override _class = 'GradingScheme';

  @Field(() => String)
  examId!: string;

  @Field(() => String)
  taskNumber!: string;

  @Field(() => String)
  criteria!: string;

  @Field(() => Float)
  maxPoints!: number;

  @Field(() => [String])
  feedback!: Maybe<string[]>;

  @Field(() => [BoundingBox])
  boundingBoxes!: Maybe<BoundingBox[]>;

  @Field(() => Float)
  confidence: Maybe<number>;

  @Field(() => Boolean)
  isApproved: Maybe<boolean>;

  /** Where this task's content came from — see SolutionSource. Absent on data written before the field existed. */
  @Field(() => String)
  source: Maybe<SolutionSource>;
}

@Entity()
export class LanguageUseScheme extends AdornisEntity {
  static override _class = 'LanguageUseScheme';

  @Field(() => String)
  _id!: Maybe<string>;

  @Field(() => String)
  criteria!: string;

  @Field(() => Float)
  maxPoints!: number;

  @Field(() => Float)
  confidence: Maybe<number>;

  @Field(() => Boolean)
  isApproved: Maybe<boolean>;

  /** taskNumbers (any depth) this rule applies to — always >=1 entries by construction, including a
   * solo (single-task) rule: every language-use rule is a LanguageUseScheme now, regardless of member count. */
  @Field(() => [String])
  dependsOn!: string[];

  /**
   * True while this rule is a 1-member placeholder carried over from a larger LanguageUseScheme group it
   * was just split off from (see updateLanguageUseSchemeMembershipMutation) — its maxPoints starts at 0
   * deliberately, since it was never individually graded (its grade lived on the group instead).
   * Consumed (cleared) by updateLanguageUseSchemeMutation the moment a teacher gives it real points, which
   * is exactly when the deferred regrade must run. Optional/absent on every rule written before this
   * field existed (or on a rule that was never split off a group).
   */
  @Field(() => Boolean)
  carriedOverFromGroup: Maybe<boolean>;

  /**
   * Small, stable, per-exam-unique number for teachers/chat to reference this group by ("Gruppe 3")
   * instead of its opaque `_id` — assigned once at creation via `allocateLanguageUseSchemeDisplayNumbers`,
   * decoupled from `_id` (which stays a permanent foreign key into `languageUseRuleResults`/grading-progress
   * and must never be reused for this). Optional/absent on every rule written before this field existed;
   * never backfilled retroactively.
   */
  @Field(() => Int)
  displayNumber: Maybe<number>;
}

@Entity()
export class ExamState extends AdornisEntity {
  static override _class = 'ExamState';

  @Field(() => Boolean, { default: v => v ?? false })
  metaDataSet!: boolean; // Handle in metadata setting logic

  @Field(() => String)
  examId!: string;

  @Field(() => UploadProgess)
  uploadProgress!: UploadProgess; // Handle in File Upload logic

  @Field(() => String)
  masterSolutionError?: string; // Error message if master solution upload failed (e.g., "PDF_ENCRYPTED")

  @Field(() => String)
  gradingSchemeError?: string; // Error message if grading scheme upload failed

  /**
   * Provenance rolled up from the tasks: 'upload' | 'generated' | 'mixed' (see SolutionSourceState).
   *
   * Denormalized on purpose. The authority is the per-task `masterSolution.source` / `gradingScheme.source`,
   * but the status bar reads the exam state alone and never loads the task tree, so the summary has to live
   * where it can see it. Recomputed by `refreshSolutionSourceState` from every path that writes a solution.
   */
  @Field(() => String)
  masterSolutionSourceState: Maybe<SolutionSourceState>;

  @Field(() => String)
  gradingSchemeSourceState: Maybe<SolutionSourceState>;

  /**
   * Live count of DISTINCT answerable tasks with at least one grading-scheme review signal — a raw
   * low-confidence OCR region, zero/negative own points (content or language-use), or a stored confidence
   * below threshold — deduped so a task tripping several signals at once still counts once (see
   * collectGradingSchemeUncertainTaskPaths). Computed on read from the exam's current tasks/language-use
   * rules/regions rather than stored, so an exam predating this field just computes its current value
   * instead of erroring or reading stale/missing data.
   */
  @Field(() => Int, {
    resolve(this: ExamState) {
      const examId = this.examId;
      return async () => {
        if (!examId) return 0;
        try {
          const examCollection = await getCollection(Exam);
          const exam = await examCollection.findOne(
            { _id: examId } as any,
            {
              projection: {
                tasks: 1,
                languageUseSchemeRules: 1,
                'examState.gradingSchemeProgress.lowConfRegions': 1,
              },
            } as any,
          );
          const tasks = (exam as any)?.tasks ?? null;
          const languageUseSchemeRules = (exam as any)?.languageUseSchemeRules ?? null;
          const lowConfRegions = (exam as any)?.examState?.gradingSchemeProgress?.lowConfRegions ?? null;
          return collectGradingSchemeUncertainTaskPaths(tasks, languageUseSchemeRules, lowConfRegions).size;
        } catch (error) {
          logger.error(
            { context: 'ExamState.gradingSchemeUncertainTaskCount', examId, error },
            'Failed to compute grading-scheme uncertainty count',
          );
          return 0;
        }
      };
    },
  })
  gradingSchemeUncertainTaskCount: Maybe<number>;

  @Field(() => Boolean, { default: v => v ?? false })
  uiNeedsRefresh!: boolean;

  /**
   * The master solution's progress, whether it was extracted from an upload or generated — one state for
   * both, so no reader has to pick between two (see SolutionProgress).
   */
  @Field(() => SolutionProgress)
  masterSolutionProgress!: SolutionProgress;

  @Field(() => SolutionProgress)
  gradingSchemeProgress!: SolutionProgress;

  @Field(() => GradingProgress)
  gradingProgress!: Maybe<GradingProgress>;

  @Field(() => TaskExtractionProgress)
  taskExtractionProgress!: TaskExtractionProgress;

  @Field(() => AnswerExtractionProgress)
  answerExtractionProgress!: AnswerExtractionProgress;

  @Field(() => [PendingFile])
  pendingFiles!: Maybe<PendingFile[]>;

  @Field(() => [PendingTask])
  pendingTasks: Maybe<PendingTask[]>;

  @Field(() => ConfirmationProgress)
  structureConfirmationState!: ConfirmationProgress;

  @Field(() => ConfirmationProgress)
  splitConfirmationState!: ConfirmationProgress;

  /**
   * PENDING until a teacher first asks the exam-creator tool to create or refine this exam — that is also
   * what the UI reads to decide whether to show the exam-creator step at all (see `setExamCreationProgressByExam`
   * in confirmation-progress.ts and `showExamCreatorStep` in chat.tsx), since an uploaded exam never touches it.
   */
  @Field(() => ConfirmationProgress)
  examCreationState!: ConfirmationProgress;

  // NO `pendingRegrade*` LEDGER HERE ANY MORE. Four fields used to accumulate what a queued re-grade should
  // announce — its causes, the submissions and task refs it covered, an approval count — and they were on the exam
  // for one stated reason: "there is no row set that means this wave". There is now (`JobDoc.batchKey`), so the asks
  // live on the run's batch and each row records what it actually did.
  //
  // The move is not cosmetic. Exam state is a SNAPSHOT — one current value per field — and a wave is a SET of past
  // events, so holding one in the other meant draining it: every bug those fields had was a property of the drain.
  // Two rows racing to take it announced twice; an entry left behind by a request that queued nothing silenced later
  // announcements for the life of the exam; and the ids it accumulated named submissions as re-graded that no row
  // had ever reached. A batch is one wave by construction and is closed by the same predicate that announces it.
  //
  // See `pipeline/regrade.ts` and `jobs/batch-store.ts`.

  @Field(() => Boolean, { default: v => v ?? false })
  hasGradingStarted!: boolean; // has grading ever been started, needed so that isCurrentlyGrading default 0 does not cause problems

  /**
   * Whether a student has handed this exam in through the online (draw-on-the-page) student-exam flow at
   * least once — see `submitStudentExamMutation`. Absent/false on every upload-only exam. This is the cheap
   * exam-level flag `uploadProgress.submissionsFound` cannot stand in for: that counter is only incremented
   * by the scan/split pipeline, never by an online submission (see exam-lock.ts's own note on it).
   *
   * Used to (a) hide the now-irrelevant "upload" progress step for an exam answered online, and (b) gate
   * master-solution/grading-scheme generation for a student's own self-practice exam until they have
   * actually handed in their attempt — see `resolveIsStudentOwnedExam` call sites in master-solution.tool.ts
   * / grading-scheme.tool.ts.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  onlineSubmissionReceived!: boolean;

  /**
   * The creator's explicit "final confirm" of a generated exam (`confirmExamCreationMutation`). Until it is
   * set the exam stays editable in the exam editor and its PDF download and online submission are withheld;
   * once set the editor is read-only (see `isExamCreatorLocked`) and both open up — otherwise a student could
   * answer, or a teacher print, a structure that no longer matches the task tree the editor later changes.
   * Exams that already carry downstream work count as confirmed without it — see `isExamCreationConfirmed`.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  examCreationConfirmed!: boolean;

  @Field(() => Int, { default: v => v ?? 0 })
  isCurrentlyGrading!: Int; // how many gradings are started at the moment quick and dirty for now ´, have to make it more detailed later
}

@Entity()
export class UploadProgess extends AdornisEntity {
  static override _class = 'UploadProgess';

  @Field(() => Float)
  totalFiles = 0;

  @Field(() => Float)
  uploadedFiles = 0;

  @Field(() => Int)
  submissionsFound = 0;
}

@Entity()
export class GradingProgress extends AdornisEntity {
  static override _class = 'GradingProgress';

  @Field(() => String)
  status!: string;

  /**
   * Every gradable unit the EXAM holds, as an estimate: gradable submissions × (main tasks + language-use
   * groups). A cartesian product, so it over-counts a submission that is missing one of the exam's tasks —
   * which is why `closeGradingSession` reconciles it DOWN to what was actually graded rather than pretending
   * the missing units happened.
   */
  @Field(() => Float)
  total = 0;

  /**
   * WHICH units are graded — `${submissionId}::${taskRef}`, one per grading call, the same shape
   * `finishedUnits` has on the extraction steps and for the same reasons.
   *
   * ONE UNIT IS ONE SUBMISSION × ONE MAIN TASK (a language-use group counts as its own ref: a group spanning
   * several member tasks is still a single grading call, not one per member). That is the granularity the
   * agent already grades at, and keeping the state at it is what lets a re-grade of two tasks say exactly
   * which units it is redoing.
   *
   * It replaced a `finished` counter whose corrections were the clearest case for sets in the codebase:
   * joining a live session did `$max(0, finished - unitsThisRun)` to hand back the units it was about to
   * redo, guessing that everything outside the run was already graded, and `closeGradingSession` then forced
   * `finished = total` to bury whatever drift that left. Pulling the exact units at the start of the run
   * needs neither the guess nor the cover-up.
   */
  @Field(() => [String])
  finishedUnits: string[] = [];

  @Field(() => String)
  message: Maybe<string>;
}

@Entity()
export class PendingFile extends AdornisEntity {
  static override _class = 'PendingFile';

  @Field(() => String)
  id!: string;

  @Field(() => [String])
  fileIds!: Maybe<string[]>;

  @Field(() => String)
  name!: Maybe<string>;

  @Field(() => String)
  mime!: Maybe<string>;

  @Field(() => Float)
  size!: Maybe<number>;

  @Field(() => String)
  status!: Maybe<string>;

  @Field(() => String)
  createdAt!: Maybe<string>;
}

/** A solution progress state with nothing done yet — the same shape extraction and generation write to. */
function solutionProgressSeed(): Record<string, unknown> {
  return {
    _class: SolutionProgress._class,
    status: StepState.NOT_STARTED,
    producer: null,
    message: '',
    total: 0,
    startedForTasks: [],
    createdForTasks: [],
    lowConfRegions: [],
    errorRegions: [],
  };
}

/**
 * Factory function to create an initialized ExamState
 * @param examId - The ID of the exam this state belongs to
 * @returns A new ExamState instance
 */
export function initializeExamState(examId: string): ExamState {
  return new ExamState({
    examId,
    metaDataSet: false,
    hasGradingStarted: false,
    onlineSubmissionReceived: false,
    examCreationConfirmed: false,
    uploadProgress: {
      _class: 'UploadProgess',
      totalFiles: 0,
      uploadedFiles: 0,
      submissionsFound: 0,
    },
    gradingProgress: {
      _class: 'GradingProgress',
      status: 'PENDING',
      total: 0,
      finishedUnits: [],
      message: '',
    },
    structureConfirmationState: {
      _class: 'ConfirmationProgress',
      status: ConfirmationProgressStatus.PENDING,
      confirmed: false,
      messageId: '',
      message: '',
    },
    splitConfirmationState: {
      _class: 'ConfirmationProgress',
      status: ConfirmationProgressStatus.PENDING,
      confirmed: false,
      messageId: '',
      message: '',
      total: 0,
      finished: 0,
    },
    examCreationState: {
      _class: 'ConfirmationProgress',
      status: ConfirmationProgressStatus.PENDING,
      confirmed: false,
      messageId: '',
      message: '',
    },
    masterSolutionProgress: solutionProgressSeed(),
    gradingSchemeProgress: solutionProgressSeed(),
    pendingFiles: [],
    // Every array field has to be initialised here. A @Field declared on ExamState but missing from this
    // factory lands on the document as null, and null is NOT the same as absent for Mongo's array
    // operators: $push/$addToSet/$pullAll all fail on it ("Cannot apply $addToSet to non-array field").
    pendingTasks: [],
  });
}
